// "The machine digests…" (docs/07 §5): summary → draft → shop → CONTINUE (always bottom-right).
import { B, B_PARAMS } from '../../content/balance';
import { PAGE_PREFIX } from '../../content/ledger';
import { DEALS, SYMBOLS } from '../../content/registry';
import { t } from '../../i18n/t';
import { controller } from '../../render';
import { feedAmount } from '../../sim/hunger';
import { hungerOf, modsOf, pricesOf, upcomingTribute } from '../../state/selectors';
import { useGame, useUI } from '../../state/store';
import type { GameState, ShopService, SymbolInstance } from '../../sim/types';
import { Bar, Btn, Frame, Icon, KText, useFmt } from '../common';
import { tip } from '../tooltip/Tooltip';
import { CharmTip } from '../hud/RightPanel';
import { CollectorTip } from '../hud/Receipt';
import { WallCorner } from './Payphone';

export function SymbolTip({ id }: { id: string }) {
  const def = SYMBOLS.get(id);
  if (!def) return null;
  return (
    <div className="col">
      <span className="h" style={{ color: '#fbb954' }}>
        {t(`sym.${id}.name`)}
      </span>
      <span className="dim">
        {def.rarity ? t(`rarity.${def.rarity}`) : ''} · {def.tags.map((x) => t(`tag.${x}`)).join(' ')}
      </span>
      <span>
        <span className="kw-value">VALUE {def.roll ? `${def.roll[0]}–${def.roll[1]}` : def.baseValue}</span>
      </span>
      <KText text={t(`sym.${id}.desc`, B_PARAMS)} />
    </div>
  );
}

function Draft({ game }: { game: GameState }) {
  const d = game.run!.intermission!.draft!;
  const prices = pricesOf(game)!;
  const fmt = useFmt();
  const mods = modsOf(game);
  const coins = game.run!.coins;
  return (
    <Frame title={t('ui.draft.title')} style={{ width: 250 }}>
      <div className="dim" style={{ marginBottom: 4 }}>
        {d.done ? t('ui.draft.done') : t('ui.draft.hint')}
      </div>
      <div className="row" style={{ justifyContent: 'flex-start', gap: 6 }}>
        {d.options.map((id, i) => {
          const page = id.startsWith(PAGE_PREFIX);
          return (
            <div
              key={`${id}${i}`}
              className={`card ${d.done ? '' : 'pick'} interactive`}
              style={{ width: 56, textAlign: 'center', opacity: d.done ? 0.4 : 1 }}
              onClick={() => !d.done && controller.act({ t: 'DRAFT_PICK', index: i })}
              data-testid={`draft-${i}`}
              {...tip(page ? <KText text={t('ui.page.draftDesc')} /> : <SymbolTip id={id} />)}
            >
              <Icon id={page ? 'item_ledger' : `sym_${id}`} style={page ? { margin: 4 } : undefined} />
              <div style={{ marginTop: 2 }}>{page ? t('ui.page.draft') : t(`sym.${id}.name`)}</div>
              {!page && <div className="dim">{t(`rarity.${SYMBOLS.get(id)?.rarity ?? 'C'}`)}</div>}
            </div>
          );
        })}
      </div>
      {!d.done && (
        <div className="row" style={{ marginTop: 6, justifyContent: 'flex-start', gap: 6 }}>
          <Btn onClick={() => controller.act({ t: 'DRAFT_SKIP' })}>
            {mods.skipDraftSouls ? t('ui.draft.skipSouls', { n: mods.skipDraftSouls }) : t('ui.draft.skip')}
          </Btn>
          <Btn disabled={coins.lt(prices.draftReroll)} onClick={() => controller.act({ t: 'DRAFT_REROLL' })}>
            {t('ui.draft.reroll', { price: fmt(prices.draftReroll) })}
          </Btn>
        </div>
      )}
    </Frame>
  );
}

function Shop({ game }: { game: GameState }) {
  const run = game.run!;
  const shop = run.intermission!.shop!;
  const prices = pricesOf(game)!;
  const mods = modsOf(game);
  const fmt = useFmt();
  const full = run.charms.length >= mods.charmSlots;
  const services: ShopService[] = ['spin', ...(mods.features.has('hold') ? ['hold' as const] : []), ...(mods.features.has('nudge') ? ['nudge' as const] : [])];
  return (
    <Frame title={t('ui.shop.title')} style={{ width: 250 }}>
      <div className="dim" style={{ marginBottom: 4 }}>
        {t('ui.shop.slots', { used: run.charms.length, max: mods.charmSlots })}
      </div>
      <div className="row" style={{ justifyContent: 'flex-start', gap: 6, alignItems: 'stretch' }}>
        {shop.charms.map((id, i) => {
          const free = shop.freeCharms > 0;
          const price = id ? prices.charm(id) : null;
          return (
            <div key={i} className="card" style={{ width: 56, textAlign: 'center' }} {...(id ? tip(<CharmTip id={id} />) : {})}>
              {id ? (
                <>
                  <Icon id={`charm_${id}`} />
                  <div style={{ margin: '2px 0 4px', minHeight: 20 }}>{t(`charm.${id}.name`)}</div>
                  <Btn
                    kind="primary"
                    disabled={full || (!free && run.coins.lt(price!))}
                    onClick={() => controller.act({ t: 'SHOP_BUY', slot: i })}
                    style={{ padding: '2px 3px 3px' }}
                    testId={`shop-${i}`}
                  >
                    {free || shop.freeAll ? t('ui.shop.free') : fmt(price!)}
                  </Btn>
                </>
              ) : (
                <div className="dim" style={{ paddingTop: 20 }}>
                  {t('ui.shop.sold')}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="col" style={{ marginTop: 6, gap: 4 }}>
        {shop.hymn && (
          <div className="row">
            <KText text={`${t('ui.shop.hymn')}: ${t('ui.shop.hymnDesc', { pattern: t(`pattern.${shop.hymn.pattern}`), mult: B.HYMN_MULT })}`} />
            <Btn disabled={shop.hymn.bought || run.coins.lt(prices.hymn)} onClick={() => controller.act({ t: 'SHOP_BUY_HYMN' })}>
              {shop.hymn.bought ? t('ui.shop.sold') : fmt(prices.hymn)}
            </Btn>
          </div>
        )}
        {services.map((s) => (
          <div className="row" key={s}>
            <span>{t(`ui.shop.service.${s}`)}</span>
            <Btn
              disabled={run.coins.lt(prices.service(s)) || (s === 'spin' && shop.bought.spin >= B.EXTRA_SPIN_MAX)}
              onClick={() => controller.act({ t: 'SHOP_SERVICE', service: s })}
            >
              {fmt(prices.service(s))}
            </Btn>
          </div>
        ))}
        <div className="row">
          <span />
          <Btn disabled={run.coins.lt(prices.shopReroll)} onClick={() => controller.act({ t: 'SHOP_REROLL' })}>
            {t('ui.shop.reroll', { price: fmt(prices.shopReroll) })}
          </Btn>
        </div>
      </div>
      {run.charms.length > 0 && (
        <div className="row" style={{ marginTop: 6, justifyContent: 'flex-start', gap: 4, flexWrap: 'wrap' }}>
          {run.charms.map((id, i) => (
            <Btn key={id} kind="ghost" onClick={() => controller.act({ t: 'SELL_CHARM', index: i })} style={{ padding: '1px 3px 2px' }}>
              <span {...tip(<CharmTip id={id} />)}>
                <Icon id={`charm_${id}`} scale={0.5} /> {t('ui.shop.sell', { price: fmt(prices.sell(id)) })}
              </span>
            </Btn>
          ))}
        </div>
      )}
    </Frame>
  );
}

/** Feed the machine (docs/04 §9 step 5): sacrifice a deck symbol, or coins. */
function Feed({ game }: { game: GameState }) {
  const run = game.run!;
  const h = hungerOf(game)!;
  const mods = modsOf(game);
  const fmt = useFmt();
  const thin = run.deck.length <= B.MIN_DECK;
  // One tile per symbol type; feeding takes the copy with the least growth.
  const groups = new Map<string, SymbolInstance[]>();
  for (const d of run.deck) groups.set(d.defId, [...(groups.get(d.defId) ?? []), d]);
  const tiles = [...groups.entries()]
    .map(([defId, list]) => ({ defId, count: list.length, inst: [...list].sort((a, b) => a.bonusValue - b.bonusValue)[0]! }))
    .sort((a, b) => (SYMBOLS.get(a.defId)?.baseValue ?? 0) - (SYMBOLS.get(b.defId)?.baseValue ?? 0));
  const coinsHunger = B.FEED_COINS_HUNGER * (1 + mods.feedPct);
  return (
    <Frame title={t('ui.feed.title')} style={{ width: 250 }}>
      <div className="row" style={{ marginBottom: 3 }} data-anchor="feedHunger">
        <span className="dim">{thin ? t('ui.feed.minDeck') : t('ui.feed.hint')}</span>
        <KText text={t('ui.feed.hunger', { n: Math.floor(h.hunger) })} />
      </div>
      <Bar value={h.hunger} max={B.HUNGER_MAX} color={h.hunger >= B.HUNGER_MAX ? 'red' : 'flesh'} />
      <div className="row" style={{ justifyContent: 'flex-start', gap: 3, flexWrap: 'wrap', marginTop: 4 }}>
        {tiles.map(({ defId, count, inst }) => (
          <div
            key={defId}
            className={`card ${thin ? '' : 'pick'} interactive`}
            style={{ width: 26, textAlign: 'center', padding: 1, opacity: thin ? 0.4 : 1 }}
            onClick={() => !thin && controller.act({ t: 'FEED_SYMBOL', uid: inst.uid })}
            data-testid={`feed-${defId}`}
            {...tip(
              <div className="col">
                <SymbolTip id={defId} />
                <KText text={`[hunger]${t('ui.feed.amount', { n: feedAmount(inst, mods) })}[/] · ${t('ui.deck.copies', { n: count })}`} />
              </div>,
            )}
          >
            <Icon id={`sym_${defId}`} scale={0.5} />
            <div className="dim">×{count}</div>
          </div>
        ))}
      </div>
      <div className="row" style={{ marginTop: 4 }}>
        <KText text={t('ui.feed.coins', { n: coinsHunger })} />
        <Btn disabled={h.hunger <= 0 || run.coins.lt(h.feedCoinsPrice)} onClick={() => controller.act({ t: 'FEED_COINS' })} testId="feed-coins">
          {h.hunger <= 0 ? t('ui.feed.full') : fmt(h.feedCoinsPrice)}
        </Btn>
      </div>
    </Frame>
  );
}

/** A Proposition (docs/04 §15): boon in ink, fine print in red. It must be answered. */
function Proposition({ game }: { game: GameState }) {
  const run = game.run!;
  const deal = run.intermission!.deal!;
  const covenant = !!game.meta.flags.covenant;
  const l = game.meta.lifetime;
  return (
    <div className="overlay interactive" style={{ inset: 0, zIndex: 5 }}>
      <div className="col" style={{ width: deal.offers.length > 1 ? 400 : 230, gap: 6 }}>
        <div className="row">
          <span className="h" style={{ color: '#e83b3b' }}>
            {t('ui.deal.title')}
          </span>
          <span className="dim">{t('ui.deal.counts', { a: l.dealsAccepted, r: l.dealsRefused })}</span>
        </div>
        <div className="row" style={{ alignItems: 'stretch', gap: 8 }}>
          {deal.offers.map((id) => (
            <div key={id} className="contract col" style={{ flex: 1, gap: 4 }} data-testid={`deal-${id}`}>
              <span className="h">{t(`deal.${id}.name`)}</span>
              <span className="dim" style={{ color: '#694f62' }}>
                {t('ui.deal.boon')}
              </span>
              <span className="boon">
                <KText text={t(`deal.${id}.boon`)} />
              </span>
              <span className="dim" style={{ color: '#694f62' }}>
                {t('ui.deal.fine')}
              </span>
              <span className="fine">{run.flags.covenant && !DEALS.get(id)?.covenant ? t('ui.deal.noFine') : <KText text={t(`deal.${id}.fine`)} />}</span>
              <span style={{ flex: 1 }} />
              <Btn kind="danger" onClick={() => controller.act({ t: 'DEAL_ACCEPT', id })} testId="deal-accept">
                {t('ui.deal.accept')}
              </Btn>
            </div>
          ))}
        </div>
        <div className="row" style={{ justifyContent: 'center' }}>
          {covenant ? (
            <span className="kw-bad">{t('ui.deal.covenantLock')}</span>
          ) : (
            <Btn onClick={() => controller.act({ t: 'DEAL_REFUSE' })} testId="deal-refuse">
              {deal.offers.length > 1 ? t('ui.deal.refuseAll') : t('ui.deal.refuse')}
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

export function Intermission() {
  const stage = useUI((u) => u.stage);
  const game = useGame((g) => g);
  const fmt = useFmt();
  const run = game.run;
  const im = run?.intermission;
  if (stage !== 'intermission' || !run || !im) return null;
  const prices = pricesOf(game)!;
  const feeding = !!hungerOf(game)?.canFeed;
  return (
    <div className="overlay interactive" style={{ alignItems: 'flex-start', paddingTop: 8 }}>
      <div className="col" style={{ width: 620, gap: 6 }}>
        <div className="row">
          <span className="h" style={{ color: '#fbb954' }}>
            {t('ui.intermission.title')}
          </span>
          <span>{t('ui.intermission.paid', { n: im.n })}</span>
        </div>
        {im.reckoning && (
          <div className="row" style={{ justifyContent: 'flex-start', gap: 14 }}>
            <span className="kw-bad">{t('ui.reckoning.beaten', { name: t(`collector.${im.reckoning.id}.name`), souls: im.reckoning.souls })}</span>
            {im.reckoning.shard && <span style={{ color: '#eaaded' }}>{t('ui.reckoning.shard')}</span>}
            {im.draft?.rareOnly && <span className="kw-value">{t('ui.reckoning.rareDraft')}</span>}
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-start', gap: 14 }}>
          <span style={{ color: '#a884f3' }}>{t('ui.intermission.souls', { n: im.souls })}</span>
          {im.early > 0 && <span className="kw-value">{t('ui.intermission.early', { coins: fmt(im.earlyBonus), spins: im.early })}</span>}
          <span>{t('ui.intermission.coins', { coins: fmt(run.coins) })}</span>
        </div>
        <div className="row" style={{ alignItems: 'flex-start', justifyContent: 'flex-start', gap: 10 }}>
          {(im.draft || feeding) && (
            <div className="col" style={{ gap: 6 }}>
              {im.draft && <Draft game={game} />}
              {feeding && <Feed game={game} />}
            </div>
          )}
          {im.shop && <Shop game={game} />}
          {!im.draft && !im.shop && <Frame style={{ width: 250 }}>{t('ui.intermission.next', { n: im.n + 1, due: fmt(prices.next) })}</Frame>}
        </div>
      </div>
      <div style={{ position: 'absolute', right: 10, bottom: 8, alignItems: 'flex-end' }} className="col">
        {im.preview.map((p) => (
          <span key={p.n} className="kw-bad" {...tip(<CollectorTip id={p.id} />)}>
            {t('ui.reckoning.next', { name: t(`collector.${p.id}.name`), n: p.n })}
          </span>
        ))}
        <span className="dim" style={{ marginBottom: 4 }}>
          {t('ui.intermission.next', { n: im.n + 1, due: fmt(upcomingTribute(game)) })}
        </span>
        <Btn kind="primary" disabled={!!im.deal && !im.deal.done} onClick={() => controller.act({ t: 'CONTINUE' })} testId="continue">
          {t('ui.continue')} ▶
        </Btn>
      </div>
      <WallCorner />
      {im.deal && !im.deal.done && <Proposition game={game} />}
    </div>
  );
}
